import * as THREE from 'three';
import { CELL } from '../core/config';
import type { Vec } from '../destruction/structure';
import type { Cluster } from '../physics/world';
import type { Person } from './population';
import type { Services } from './services';

interface Job {
  crew: Person;
  key: string;
  patient?: Person;
  cluster?: Cluster;
  goal: THREE.Vector3;
  started: number;
  progress: number;
  best: number;
  worked: number;
  probe: number;
  approach?: THREE.Vector3;
  travelBest?: number;
  lastUsed?: number;
}

/** Auxiliary crews use the same patient identities and supported foot planner
 * as medics. Clearance pushes original loose bodies; it never deletes rubble. */
export class IncidentAssistance {
  readonly jobs = new Map<string, Job>();
  private claimed = new Set<string>();
  private retry = new Map<string, number>();
  private rejected = new Map<string, number>();
  private time = 0;
  aided = 0;
  cleared = 0;
  pushedMetres = 0;
  constructor(readonly services: Services) {}
  update(dt: number): void {
    this.time += dt;
    for (const job of this.jobs.values())
      if (job.crew.fatal || job.crew.hidden || this.time - (job.lastUsed ?? job.started) > 2)
        this.release(job.crew);
    for (const [key, until] of this.rejected) if (until < this.time) this.rejected.delete(key);
  }
  clear(): void {
    for (const job of this.jobs.values()) this.services.releaseAssistingCrew(job.crew);
    this.jobs.clear();
    this.claimed.clear();
    this.retry.clear();
    this.rejected.clear();
    this.time = this.aided = this.cleared = this.pushedMetres = 0;
  }
  release(crew: Person, failed = false): void {
    const job = this.jobs.get(crew.id);
    if (job) {
      this.claimed.delete(job.key);
      if (failed) this.rejected.set(`${crew.id}/${job.key}`, this.time + 12);
    }
    this.jobs.delete(crew.id);
    this.services.releaseAssistingCrew(crew);
    this.retry.set(crew.id, this.time + 1.5);
  }
  private closest(c: Cluster, from: Vec): THREE.Vector3 {
    const physics = this.services.physics,
      local = physics.toLocal(c, from);
    let best = new THREE.Vector3(c.body.translation().x, c.body.translation().y, c.body.translation().z),
      score = Infinity;
    for (const box of c.collisionBoxes) {
      const point = physics.toWorld(c, {
        x: Math.max(box.x * CELL, Math.min((box.x + box.w) * CELL, local.x)),
        y: Math.max(box.y * CELL, Math.min((box.y + box.h) * CELL, local.y)),
        z: Math.max(box.z * CELL, Math.min((box.z + box.d) * CELL, local.z)),
      });
      const d = (point.x - from.x) ** 2 + (point.y - from.y) ** 2 + (point.z - from.z) ** 2;
      if (d < score) {
        score = d;
        best = point;
      }
    }
    return best;
  }
  private choose(crew: Person, incident: Vec): Job | undefined {
    const { population, physics } = this.services;
    const available = (key: string) =>
      !this.claimed.has(key) && (this.rejected.get(`${crew.id}/${key}`) ?? 0) <= this.time;
    const people: Person[] = [];
    population.actorsNear(incident, 80, (actor) => {
      if (
        actor.category === 'person' &&
        actor.state === 'incapacitated' &&
        !actor.fatal &&
        !actor.controlled &&
        !actor.hidden &&
        !this.services.fieldStabilised.has(actor.id) &&
        available(`aid:${actor.id}`)
      )
        people.push(actor);
    });
    people.sort((a, b) => a.p.distanceToSquared(crew.p) - b.p.distanceToSquared(crew.p));
    for (const patient of people.slice(0, 4)) {
      const goal = this.services.patientApproach(patient, crew.p);
      if (goal)
        return {
          crew,
          patient,
          key: `aid:${patient.id}`,
          goal,
          started: this.time,
          progress: this.time,
          best: crew.p.distanceTo(goal),
          worked: 0,
          probe: 0,
        };
    }
    if ([...this.jobs.values()].filter((j) => j.cluster).length >= 8) return;
    const chunks = [...physics.clusters.values()].filter(
      (c) =>
        !c.anchored &&
        c.radius < 4.5 &&
        c.building.kind !== 'road' &&
        c.building.kind !== 'bridge' &&
        c.building.category !== 'bridge' &&
        c.cells.size <= 32 &&
        c.body.linvel().y < 0.5 &&
        available(`rubble:${c.id}`) &&
        Math.hypot(c.body.translation().x - incident.x, c.body.translation().z - incident.z) < 70 &&
        Math.abs(c.body.translation().y - crew.p.y) < 4,
    );
    chunks.sort(
      (a, b) =>
        crew.p.distanceToSquared(a.body.translation()) - crew.p.distanceToSquared(b.body.translation()),
    );
    for (const c of chunks.slice(0, 8)) {
      const centre = c.body.translation();
      // Select the actual obstructed road, including closed edges. The road
      // planner intentionally excludes it, so cannot supply this worksite.
      let nearest: { point: Vec; nx: number; nz: number; width: number; distance: number } | undefined;
      for (const edge of population.network.edges.values()) {
        const a = population.network.nodes.get(edge.from)!,
          b = population.network.nodes.get(edge.to)!;
        const dx = b.x - a.x,
          dz = b.z - a.z,
          length = Math.hypot(dx, dz);
        const t = Math.max(
          0,
          Math.min(1, ((centre.x - a.x) * dx + (centre.z - a.z) * dz) / (length * length)),
        );
        const point = {
          x: a.x + dx * t,
          y: population.network.height(edge, centre.x, centre.z),
          z: a.z + dz * t,
        };
        const distance = Math.hypot(point.x - centre.x, point.z - centre.z);
        if (
          Math.abs(point.y - centre.y) < 4 &&
          distance < edge.width / 2 + c.radius &&
          (!nearest || distance < nearest.distance)
        )
          nearest = { point, nx: -dz / length, nz: dx / length, width: edge.width + 4, distance };
      }
      if (!nearest) continue;
      const signed = (centre.x - nearest.point.x) * nearest.nx + (centre.z - nearest.point.z) * nearest.nz;
      for (const side of [signed < 0 ? -1 : 1, signed < 0 ? 1 : -1]) {
        const distance = nearest.width / 2 + c.radius + 1.5;
        const goal = new THREE.Vector3(
          nearest.point.x + nearest.nx * side * distance,
          crew.p.y,
          nearest.point.z + nearest.nz * side * distance,
        );
        const support = physics.ground(goal, 0.4, 0.5);
        if (!support || physics.blocked(goal.x, goal.z, c.radius * 0.5, support.y + 0.5)) continue;
        goal.y = centre.y;
        return {
          crew,
          cluster: c,
          key: `rubble:${c.id}`,
          goal,
          started: this.time,
          progress: this.time,
          best: Infinity,
          worked: 0,
          probe: 0,
        };
      }
    }
  }
  step(crew: Person, incident: Vec, dt: number): boolean {
    if (
      crew.hidden ||
      crew.fatal ||
      ['falling', 'incapacitated', 'carried', 'recovering'].includes(crew.state)
    ) {
      this.release(crew);
      return false;
    }
    let job = this.jobs.get(crew.id);
    if (!job && this.jobs.size < 8 && this.time >= (this.retry.get(crew.id) ?? 0)) {
      this.retry.set(crew.id, this.time + 2.5);
      job = this.choose(crew, incident);
      if (job) {
        this.jobs.set(crew.id, job);
        this.claimed.add(job.key);
      }
    }
    if (!job) return false;
    job.lastUsed = this.time;
    if (this.services.assistanceRouteFailed(crew)) {
      this.release(crew, true);
      return false;
    }
    if (this.time - job.progress > 12 || this.time - job.started > 75) {
      this.release(crew, true);
      return false;
    }
    if (job.patient) {
      const patient = job.patient;
      if (patient.fatal || patient.controlled || patient.state !== 'incapacitated') {
        this.release(crew);
        return false;
      }
      if (this.services.canTreat(crew, patient)) {
        crew.state = 'assessing';
        job.worked += dt;
        job.progress = this.time;
        if (job.worked >= 3) {
          this.services.fieldStabilised.add(patient.id);
          this.aided++;
          for (const i of this.services.incidents.values())
            if (i.people.has(patient.id)) this.services.observeIncident(i.id);
          this.release(crew);
        }
        return true;
      }
    } else if (job.cluster) {
      const c = job.cluster,
        physics = this.services.physics;
      if (physics.clusters.get(c.id) !== c || c.anchored) {
        this.release(crew);
        return false;
      }
      const centre = c.body.translation(),
        distance = Math.hypot(centre.x - job.goal.x, centre.z - job.goal.z);
      if (distance < 1.4) {
        this.cleared++;
        this.release(crew);
        return true;
      }
      const contact = this.closest(c, { x: crew.p.x, y: crew.p.y + 0.65, z: crew.p.z });
      if (
        contact.distanceTo(new THREE.Vector3(crew.p.x, crew.p.y + 0.65, crew.p.z)) < 1.65 &&
        physics.ground(crew.p, 0.2, 0.35)
      ) {
        const direction = new THREE.Vector3(job.goal.x - centre.x, 0, job.goal.z - centre.z).normalize();
        let clear = true;
        this.services.population.actorsNear(centre, c.radius + 2, (a) => {
          if (
            a.id !== crew.id &&
            !a.hidden &&
            a.category === 'person' &&
            (a.p.x - centre.x) * direction.x + (a.p.z - centre.z) * direction.z > 0
          )
            clear = false;
        });
        if (clear) {
          const velocity = c.body.linvel(),
            along = velocity.x * direction.x + velocity.z * direction.z;
          physics.impulse(c, centre, direction, Math.max(0, 0.85 - along) * Math.min(1, dt * 20));
          // Supported manual handling raises loose pieces just enough to get
          // over a curb. Pure horizontal pushing wedged them against a 12 cm
          // pavement edge forever. Use forces on the original dynamic body,
          // with gravity/collisions still active and no position correction.
          const floor = physics.ground(crew.p, 0.2, 0.35);
          let bottom = Infinity;
          for (const box of c.collisionBoxes)
            for (const x of [box.x, box.x + box.w])
              for (const y of [box.y, box.y + box.h])
                for (const z of [box.z, box.z + box.d])
                  bottom = Math.min(bottom, physics.toWorld(c, { x: x * CELL, y: y * CELL, z: z * CELL }).y);
          if (floor && bottom < floor.y + 0.4) {
            const acceleration = Math.max(
              -12,
              Math.min(20, 9.8 + (floor.y + 0.22 - bottom) * 40 - velocity.y * 9),
            );
            c.body.applyImpulse({ x: 0, y: c.body.mass() * acceleration * dt, z: 0 }, true);
          }
          crew.state = 'clearing rubble';
          if (distance + 0.002 < job.best) {
            this.pushedMetres += job.best === Infinity ? 0 : job.best - distance;
            job.progress = this.time;
            job.best = distance;
          }
        }
      }
      if (this.time >= job.probe) {
        job.probe = this.time + 1;
        const direction = new THREE.Vector3(crew.p.x - centre.x, 0, crew.p.z - centre.z).normalize();
        const edge = this.closest(c, crew.p.clone().addScaledVector(direction, c.radius + 3));
        const point = edge.addScaledVector(direction, 0.85);
        point.y = crew.p.y;
        const floor = physics.ground(point, 0.35, 0.6);
        if (floor && !physics.blocked(point.x, point.z, 0.27, floor.y + 0.8)) {
          point.y = floor.y;
          job.approach = point;
        }
      }
      if (!job.approach) return true;
    }
    const goal = job.approach ?? job.goal;
    const distance = crew.p.distanceTo(goal);
    if (distance + 0.15 < (job.travelBest ?? Infinity)) {
      job.travelBest = distance;
      job.progress = this.time;
    }
    if (!job.cluster && distance + 0.15 < job.best) {
      job.best = distance;
      job.progress = this.time;
    }
    this.services.walkAssistingCrew(crew, goal, dt);
    return true;
  }
}
